import { createApi } from '@reduxjs/toolkit/query/react';

import { setError } from './configurationSlice';
import { IDelayConfiguration } from '../types/ISettings';

import {
  createNotificationHandler,
  MESSAGES,
  SCOPES,
} from '@/redux/notificationHandler';
import { axiosBaseQuery } from '@/redux/axios';

export const delayApi = createApi({
  reducerPath: 'delayApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Delays'],
  endpoints: (builder) => ({
    getDelayConfigurations: builder.query<IDelayConfiguration[], void>({
      query: () => ({
        url: 'Configurations/Delays',
        method: 'get',
      }),
      providesTags: ['Delays'],
      onQueryStarted: createNotificationHandler(
        setError,
        SCOPES.SETTINGS_MINOR_DELAY,
        MESSAGES.LOAD,
        false
      ),
    }),
    createDelayConfiguration: builder.mutation<
      IDelayConfiguration,
      Partial<IDelayConfiguration>
    >({
      query: (configuration) => ({
        url: 'Configurations/Delays',
        method: 'post',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.ADD
        )(configuration, { dispatch, queryFulfilled });

        const { data: newItem } = await queryFulfilled;
        dispatch(
          delayApi.util.updateQueryData(
            'getDelayConfigurations',
            undefined,
            (draft) => {
              draft.push(newItem);
            }
          )
        );
      },
    }),
    updateDelayConfiguration: builder.mutation<
      IDelayConfiguration,
      IDelayConfiguration
    >({
      query: (configuration) => ({
        url: `Configurations/Delays/${configuration.id}`,
        method: 'put',
        data: configuration,
      }),
      async onQueryStarted(configuration, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.UPDATE
        )(configuration, { dispatch, queryFulfilled });

        const { data: updatedItem } = await queryFulfilled;
        dispatch(
          delayApi.util.updateQueryData(
            'getDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === updatedItem.id);
              if (idx !== -1) {
                draft[idx] = { ...draft[idx], ...updatedItem };
              }
            }
          )
        );
      },
    }),
    removeDelayConfiguration: builder.mutation<
      { success: boolean },
      { id: string | number }
    >({
      query: ({ id }) => ({
        url: `Configurations/Delays/${id}`,
        method: 'delete',
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        createNotificationHandler(
          setError,
          SCOPES.SETTINGS_MINOR_DELAY,
          MESSAGES.DELETE
        )({ id }, { dispatch, queryFulfilled });

        await queryFulfilled;
        dispatch(
          delayApi.util.updateQueryData(
            'getDelayConfigurations',
            undefined,
            (draft) => {
              const idx = draft.findIndex((item) => item.id === id);
              if (idx !== -1) {
                draft.splice(idx, 1);
              }
            }
          )
        );
      },
    }),
  }),
});

export const {
  useGetDelayConfigurationsQuery,
  useCreateDelayConfigurationMutation,
  useUpdateDelayConfigurationMutation,
  useRemoveDelayConfigurationMutation,
} = delayApi;

export default delayApi;
